import { api } from '../api/client';

import { API_COLLECTIONS_URL, API_URL } from '../utils';
import { definitionPayload } from './shareLinks';

/**
 * PERMANENT PUBLIC FORMS (KPR-513; keepr-api docs/SCHEMA.md § Public forms,
 * built by KPR-471 and KPR-512). The second kind of public form: a readable
 * address that never expires — `/f/<collection-slug>/<form-slug>` — where an
 * invite link is a secret code with an expiry.
 *
 * Two halves with opposite postures, like share links:
 *   - the MANAGE half (`/api/collections/{id}/public-forms…`) goes through
 *     the shared client: a manager's session, a verified account for writes;
 *   - the PUBLIC half (`/api/forms/{collectionSlug}/{formSlug}…`) never does.
 *     Each call is a plain fetch (an XHR for an upload, for its progress)
 *     with no credentials and no Authorization header. Two reasons, both
 *     paid for already on the share-link page (knowledge: web invariants, "A
 *     public page's bytes go by plain URL"): the shared client attaches
 *     whatever bearer is in this browser to every request — the API's public
 *     routes are `auth: false` and cannot act as that person, but the web
 *     should not hand it over either — and its governor reads a 429 on a GET
 *     as "slow down" and holds every later GET for up to a minute, where this
 *     address's GET answers 429 `form_limit_reached` with a Retry-After of up
 *     to an hour by design.
 *
 * A refusal from the public half throws an axios-shaped error (`response:
 * { status, data, headers }`), so the page reads it as it reads any other.
 */

const formsUrl = collectionId => `${API_COLLECTIONS_URL}/${collectionId}/public-forms`;
const formUrl = (collectionId, formId) => `${formsUrl(collectionId)}/${formId}`;

// ------------------------------------------------------------------ manage

/**
 * `GET …/public-forms` → `{ collectionSlug, suggestedCollectionSlug,
 * available, unavailable, notifications, forms }`. It is also the web's only way to know the
 * feature exists: an API without it answers 404, and one whose server switch
 * is off answers `unavailable: 'public_forms_off'` (hooks/usePublicForms).
 */
export async function listPublicForms(collectionId) {
	const { data } = await api.get(formsUrl(collectionId));
	return {
		collectionSlug: typeof data?.collectionSlug === 'string' ? data.collectionSlug : null,
		suggestedCollectionSlug: typeof data?.suggestedCollectionSlug === 'string' ? data.suggestedCollectionSlug : null,
		available: data?.available === true,
		unavailable: typeof data?.unavailable === 'string' ? data.unavailable : null,
		forms: Array.isArray(data?.forms) ? data.forms : [],
		// KPR-514: keepr's two notifications about this collection's forms,
		// `[{ key, name, on }]` — empty until the owner has written them
		// (`ensureFormNotificationDefs`), and absent from an older API.
		notifications: Array.isArray(data?.notifications)
			? data.notifications.filter(row => row && typeof row.key === 'string')
			: [],
	};
}

const limitsPayload = (limits) => {
	if (!limits || typeof limits !== 'object') return undefined;
	const out = {};
	['perDay', 'perWeek', 'maxPending'].forEach((key) => {
		if (Number.isInteger(limits[key])) out[key] = limits[key];
	});
	return Object.keys(out).length ? out : undefined;
};

const trimmed = value => (typeof value === 'string' ? value.trim() : '');

/**
 * `POST …/public-forms` → 201 the form. With `slug` it is LIVE at its
 * address the moment it lands; `collectionSlug` only while the collection has
 * none (it is chosen once). `acknowledge: true` is the manager saying they
 * know anyone with the address can send a submission — the API refuses the
 * create without it.
 */
export async function createPublicForm(collectionId, {
	name, cardId, definition, limits, slug, collectionSlug, acknowledge,
} = {}) {
	const payload = {
		name: trimmed(name),
		card_id: cardId,
		definition: definitionPayload(definition),
		acknowledge: acknowledge === true,
	};
	const limited = limitsPayload(limits);
	if (limited) payload.limits = limited;
	if (trimmed(slug)) payload.slug = trimmed(slug);
	if (trimmed(collectionSlug)) payload.collectionSlug = trimmed(collectionSlug);
	const { data } = await api.post(formsUrl(collectionId), payload);
	return data;
}

/**
 * `PATCH …/public-forms/{formId}` → the form. `definition` and `limits` merge
 * per key over the stored ones and the merge is judged whole; the copy goes
 * up as null when cleared (an edit), exactly as an intake link's edit does.
 * Never the state, the address or the card.
 */
export async function updatePublicForm(collectionId, formId, { name, definition, limits } = {}) {
	const payload = {};
	if (name !== undefined) payload.name = trimmed(name);
	if (definition) payload.definition = definitionPayload(definition, { edit: true });
	const limited = limitsPayload(limits);
	if (limited) payload.limits = limited;
	const { data } = await api.patch(formUrl(collectionId, formId), payload);
	return data;
}

/** `POST …/pause` → the form, paused by a manager. Idempotent; never gated. */
export async function pausePublicForm(collectionId, formId) {
	const { data } = await api.post(`${formUrl(collectionId, formId)}/pause`);
	return data;
}

/** `POST …/resume` → the form, live. Passes every going-live gate (the switch, the bot check, the collection's). */
export async function resumePublicForm(collectionId, formId) {
	const { data } = await api.post(`${formUrl(collectionId, formId)}/resume`);
	return data;
}

/**
 * `PUT …/address` → the form at `/f/{collectionSlug}/{slug}`: a new address,
 * or another form's of this collection MOVED here (that form is left without
 * one). A draft goes live here, which needs `acknowledge: true`.
 */
export async function setPublicFormAddress(collectionId, formId, { slug, collectionSlug, acknowledge } = {}) {
	const payload = { slug: trimmed(slug) };
	if (trimmed(collectionSlug)) payload.collectionSlug = trimmed(collectionSlug);
	if (acknowledge === true) payload.acknowledge = true;
	const { data } = await api.put(`${formUrl(collectionId, formId)}/address`, payload);
	return data;
}

/** `DELETE …/{formId}` → the form, retired — final. Its address keeps its reservation and points at nothing. */
export async function retirePublicForm(collectionId, formId) {
	const { data } = await api.delete(formUrl(collectionId, formId));
	return data;
}

/**
 * Submission volume (KPR-512, SCHEMA § Volume, for both kinds): `{ source,
 * days, granularity: 'hour' | 'day', from, to, buckets: [{ at, submitted,
 * accepted, rejected, botFailed, limited, reports }], totals }`, zero-filled, hourly
 * for two days or fewer, else by UTC day. `kind` 'form' reads a public form,
 * 'link' an invite link.
 */
export async function getFormVolume(collectionId, { kind, id }, days = 30) {
	const url = kind === 'link'
		? `${API_COLLECTIONS_URL}/${collectionId}/share-links/${id}/volume`
		: `${formUrl(collectionId, id)}/volume`;
	const { data } = await api.get(url, { params: { days } });
	return data;
}

// ------------------------------------------------------------------ public

const addressUrl = (collectionSlug, formSlug) => `${API_URL}/forms/${encodeURIComponent(collectionSlug || '')}/${encodeURIComponent(formSlug || '')}`;

/** The error a public call throws: axios-shaped, so callers read it as any other. */
function publicCallError(status, data, retryAfter = null, fallback = 'That did not go through.') {
	const error = new Error((data && data.message) || fallback);
	error.response = { status, data, headers: retryAfter ? { 'retry-after': retryAfter } : {} };
	return error;
}

async function publicJson(url, init = {}) {
	let res;
	try {
		res = await fetch(url, { ...init, credentials: 'omit' });
	} catch {
		throw publicCallError(0, null, null, 'This form could not reach keepr. Check the connection and try again.');
	}
	const data = await res.json().catch(() => null);
	if (!res.ok) {
		const retryAfter = typeof res.headers?.get === 'function' ? res.headers.get('Retry-After') : null;
		throw publicCallError(res.status, data, retryAfter);
	}
	return data;
}

/**
 * The form at an address — no account. Answers the intake form's shape with
 * `kind: 'public'` (no `expiresAt`, `botCheck` always set, a submission token
 * in the form scope), or `{ kind: 'public', paused: true, collection: { name
 * }, headline }` and nothing else. Throws 404 (the one flat body for every
 * dead address), 400 `form_out_of_date`, 429 `form_limit_reached`.
 * `?uploads=1`: this page can send photos, so file elements are served.
 * `?shown=1` (KPR-519): this page renders read-only elements, so the form's
 * shown elements are served too (services/shareLinks getSharedView).
 */
export function getPublicForm(collectionSlug, formSlug) {
	return publicJson(`${addressUrl(collectionSlug, formSlug)}?uploads=1&shown=1`, { method: 'GET' });
}

/**
 * Send it: `{ submissionToken, turnstileToken, elements }` → `{ ok,
 * confirmation, allowAnother }`. The payload is closed, as the intake
 * submit's. Besides the intake submit's refusals: 409 `form_paused`, 429
 * `form_limit_reached`.
 */
export function submitPublicForm(collectionSlug, formSlug, {
	submissionToken, turnstileToken, elements,
} = {}) {
	const payload = { submissionToken, elements: elements || {} };
	if (turnstileToken) payload.turnstileToken = turnstileToken;
	return publicJson(`${addressUrl(collectionSlug, formSlug)}/submit`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload),
	});
}

/**
 * One file to the form: the bytes as the body, the submission token in
 * `X-Submission-Token` — and, unlike an invite link's, a FRESH Turnstile
 * token in `cf-turnstile-response` on every upload (KPR-512 review A1: a
 * form's submission token is free, one GET away, so it proves nothing on its
 * own). A Turnstile token is single use; the page takes a new one per file.
 * XMLHttpRequest for its progress, with no credentials.
 */
export function uploadPublicFormFile(collectionSlug, formSlug, {
	file, element, submissionToken, turnstileToken,
} = {}, { onProgress, signal } = {}) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', `${addressUrl(collectionSlug, formSlug)}/uploads?element=${encodeURIComponent(element || '')}`);
		xhr.withCredentials = false;
		xhr.setRequestHeader('X-Submission-Token', submissionToken || '');
		xhr.setRequestHeader('cf-turnstile-response', turnstileToken || '');
		xhr.setRequestHeader('Content-Type', (file && file.type) || 'application/octet-stream');
		if (xhr.upload && typeof onProgress === 'function') {
			xhr.upload.onprogress = (event) => {
				if (event && event.lengthComputable && event.total > 0) onProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
			};
		}
		xhr.onload = () => {
			let data = null;
			try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { data = null; }
			if (xhr.status >= 200 && xhr.status < 300) { resolve(data); return; }
			reject(publicCallError(xhr.status, data, xhr.getResponseHeader('Retry-After'), `The upload was refused (${xhr.status}).`));
		};
		xhr.onerror = () => reject(publicCallError(0, null, null, 'The upload did not reach keepr. Check the connection and try again.'));
		xhr.onabort = () => {
			const error = publicCallError(0, null, null, 'The upload was stopped.');
			error.aborted = true;
			reject(error);
		};
		if (signal) {
			if (signal.aborted) { xhr.abort(); return; }
			signal.addEventListener('abort', () => xhr.abort(), { once: true });
		}
		xhr.send(file);
	});
}

/** Take one of this form's uploads back (allowed while the form is paused). Needs only the submission token. */
export async function removePublicFormUpload(collectionSlug, formSlug, uploadId, submissionToken) {
	await publicJson(`${addressUrl(collectionSlug, formSlug)}/uploads/${encodeURIComponent(uploadId)}`, {
		method: 'DELETE',
		headers: { 'X-Submission-Token': submissionToken || '' },
	});
	return true;
}

/**
 * "Report this form" (KPR-514): `{ reason, text?, turnstileToken }` → `{ ok:
 * true }`, the same whether or not the report paused the form. The token
 * must be a FRESH one from the report dialog's own widget, never the one the
 * form's Send is holding: a Turnstile token is single use, and spending the
 * submit's here would fail the visitor's next send. Refusals: 400
 * `invalid_reason` / `text_too_long` / `challenge_failed`, 404 the flat body,
 * 409 `form_paused`, 429 `rate_limited`, 503 `challenge_unavailable`.
 */
export function reportPublicForm(collectionSlug, formSlug, { reason, text, turnstileToken } = {}) {
	const payload = { reason, turnstileToken: turnstileToken || '' };
	if (typeof text === 'string' && text.trim()) payload.text = text.trim();
	return publicJson(`${addressUrl(collectionSlug, formSlug)}/report`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload),
	});
}
